// Working a deal from Deals: the routes of apps/api/src/routes/deal-work.ts (the Deal work lane; W2's add, U259, is
// retired from the portal by W6's Add deal page, whose routes are here). A module of this lane's own beside
// deals/api.ts, so the lane and the Deals page never share a file (DEAL-WORK-BRIEF-2026-10-04.md section 7).
import { viewFetch as fetch } from '../view-as.js';
import { Unauthenticated } from '../api.js';
import type { Opens } from '../deals/api.js';

/** A refusal, with what it names: the opportunity already open, or the store's company of the typed name. */
export class DealWorkError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly open: { dealId: string | null; title: string; accountId: string | null } | null = null,
    readonly company: { id: string; name: string } | null = null,
  ) { super(message); }
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const b = (await res.json().catch(() => null)) as { error?: unknown; open?: DealWorkError['open']; company?: DealWorkError['company'] } | null;
    throw new DealWorkError(res.status, typeof b?.error === 'string' ? b.error : `${res.status} ${res.statusText}`, b?.open ?? null, b?.company ?? null);
  }
  return (await res.json()) as T;
}

const post = (body: unknown): RequestInit => ({
  method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
});

/** The matter that looks like the same deal (W3, U260), or the thread filed under no matter (W5), with what the reader can see of it. */
export interface SameDeal {
  matterId: string | null; thread: string | null; title: string; hubspotDealId: string | null;
  items: number; messages: number; meetings: number; documents: number; commitments: number; since: string | null;
}
/** What "Bring its history here" and "Not this one" name: the matter, or the thread. */
export type HistoryOf = { matterId: string } | { thread: string };
/** The company's mail domain put to the reader (W5, deals/work/domain.ts). */
export interface DomainToConfirm { organisationId: string; company: string; domain: string; items: number; addresses: number }
export interface DomainAdded { dealId: string; actionId: string; domain: string; company: string }

/** How the deal started (W6, deals/work/origins.ts): what the reader may read of each part. */
export interface OriginMessage { signalId: string; kind: string; subject: string | null; from: string | null; at: string; opens: Opens | null }
export interface Origins {
  first: OriginMessage | null;
  show: { name: string; startsOn: string | null; endsOn: string | null; meeting: OriginMessage | null } | null;
  notes: { ref: string | null; metBy: string | null; said: string | null; terms: string | null; route: string | null } | null;
  hubspot: { title: string; stage: string | null; closeOn: string | null; putAsideAt: string | null; joinedAt: string | null; joinedBy: string | null; self: boolean } | null;
}
export interface Joined { dealId: string; actionId: string; title: string; moved: { filings: number; commitments: number; tasks: number } }

/** The card's options for a deal (W4, U258): the message they answer, and each option's draft purpose (deals/work/options.ts). */
export interface DealOption { key: string; kind: 'draft' | 'reply' | 'chase'; label: string; note: string; purpose: 'reply' | 'chase'; signalId: string; box: boolean }
export interface DealLatest { signalId: string; subject: string | null; at: string; who: string | null; ours: boolean; mine: boolean }
export interface DealSteps { latest: DealLatest | null; options: DealOption[] }

/** Add deal, a page of its own (W6, U285 to U289, U293, U295, U297): what the page chooses from (deals/work/add-form.ts). */
export interface NewFormStage { code: string; name: string }
export interface NewFormPerson { principalId: string; name: string }
export interface NewFormProperty {
  id: string; sport: string; name: string; offeredBy: string | null; holderId: string | null; holder: string | null;
  territory: string | null; matchesVideo: number | null; matchesData: number | null; status: string | null; held: boolean;
}
export interface NewForm {
  mayAdd: boolean; me: NewFormPerson | null; families: string[]; stages: NewFormStage[];
  companies: Array<{ id: string; name: string; rightsHolder: boolean }>; team: NewFormPerson[];
  shows: Array<{ id: string; name: string; startsOn: string }>; properties: NewFormProperty[];
}
export interface OpenDeal {
  dealId: string; title: string; product: string | null; family: string; stage: string; owner: string | null; mine: boolean; accountId: string;
  /** BUGS.md D108: a parked deal is 'paused', with the park's date and its reason; absent from an older API, read as open. */
  lifecycle?: 'open' | 'paused'; pausedUntil?: string | null; stateReason?: string | null;
}
/** A message of a thread already filed under another deal: listed with that deal's name, unticked (pipeline FT1, D32). */
export interface FiledElsewhere { signalId: string; subject: string; at: string; dealId: string | null; dealTitle: string }
export interface LooseThread { thread: string; title: string; who: string | null; at: string; meeting: boolean; items: number; signalIds: string[]; elsewhere: FiledElsewhere[] }
export interface CompanyColumn {
  company: { id: string; name: string; type: string | null; class: string; owner: string | null; domains: string[]; agreements: number; rightsHolder: boolean };
  open: OpenDeal[];
  openFamilies: Record<string, OpenDeal>;
  people: Array<{ id: string; name: string; email: string | null }>;
  threads: LooseThread[];
  /** BUGS.md D110: the loose threads `threads` leaves out, of the last 18 months and older; absent from an older API. */
  threadsMore?: { recent: number; older: number };
  /** Set once every loose thread is listed (newFormThreads): the newest 200 were, and there are more. */
  threadsCapped?: boolean;
  latest: Array<{ said: string; at: string | null; kind: string }>;
  suggested: { at: string; kind: string; step: string } | null;
}
export interface AddressCheck { domain: string | null; free: boolean; owner: { id: string; name: string } | null; named: { id: string; name: string } | null; held: number }

export type PayBasis = 'fixed' | 'share' | 'per_match' | 'unknown';
export type Period = 'year' | 'month';
export interface PayBody {
  basis: PayBasis; currency: string;
  fee?: number | null; feePeriod?: Period | null; sharePct?: number | null; shareOf?: string | null;
  minimum?: number | null; minimumPeriod?: Period | null; base?: number | null; basePeriod?: Period | null;
  matchRate?: number | null; matchCount?: number | null; expected?: number | null;
}
export interface NewDealBody {
  side: 'sales' | 'rights';
  company: { id: string } | { new: { name: string; brand: string | null; email: string; phone: string | null } };
  /** Each product's own Expected revenues ride with its line (B2251); the rights side's fee is the add's own pay. */
  lines: Array<{ family: string | null; product: string | null; stage: string; keenness: number; ownerId: string; pay?: PayBody | null }>;
  rights: null | {
    propertyId?: string; newProperty?: { sport: string; name: string; territory: string | null };
    video: boolean; data: boolean; exclusive: 'yes' | 'no' | 'unknown'; territory: string | null; termFrom: string | null; termUntil: string | null;
  };
  step: string; by: string; contactId: string | null;
  origin: 'pitched' | 'enquiry' | 'referral' | null; where: 'show' | 'meeting' | 'call' | 'mail' | null; showId: string | null;
  said: string | null; pay: PayBody | null; file: string[];
}
export interface DealsAdded {
  deals: Array<{ dealId: string; matterId: string; title: string; product: string | null; stage: string; owner: string; ownerMine: boolean; taskId: string }>;
  organisationId: string; organisationName: string; accountId: string; newCompany: boolean; filed: number; actionId: string;
}
/** An Add refused, with what it names: the deal already open, the company of that name, or the company holding the domain. */
export class AddDealError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly open: OpenDeal | null = null,
    readonly company: { id: string; name: string } | null = null,
    readonly domain: string | null = null,
  ) { super(message); }
}
async function added(res: Response): Promise<DealsAdded> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const b = (await res.json().catch(() => null)) as { error?: unknown; open?: OpenDeal; company?: { id: string; name: string }; domain?: string } | null;
    throw new AddDealError(res.status, typeof b?.error === 'string' ? b.error : `${res.status} ${res.statusText}`, b?.open ?? null, b?.company ?? null, b?.domain ?? null);
  }
  return (await res.json()) as DealsAdded;
}

export const dealWorkApi = {
  newForm: (store: string) =>
    fetch(`/api/deals/new-form?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' }).then((r) => json<NewForm>(r)),
  newFormCompany: (store: string, id: string) =>
    fetch(`/api/deals/new-form/company/${encodeURIComponent(id)}?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' }).then((r) => json<CompanyColumn>(r)),
  /** BUGS.md D110: every conversation at the company filed under no deal, of any age, the newest 200. */
  newFormThreads: (store: string, id: string) =>
    fetch(`/api/deals/new-form/company/${encodeURIComponent(id)}/threads?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' })
      .then((r) => json<{ threads: LooseThread[]; capped: boolean }>(r)),
  /** A POST, so the address typed is never in a logged URL. */
  checkAddress: (store: string, email: string, name: string, brand: string) =>
    fetch('/api/deals/new-form/address', post({ store, email, name, brand })).then((r) => json<AddressCheck>(r)),
  addDeal: (store: string, body: NewDealBody) =>
    fetch('/api/deals/new', post({ store, ...body })).then(added),
  undoAddDeal: (store: string, dealId: string, actionId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/new/undo`, post({ store, actionId })).then((r) => json<{ dealId: string; undone: true }>(r)),
  sameDeal: (store: string, dealId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/same-deal?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' }).then((r) => json<{ sameDeal: SameDeal | null; domain?: DomainToConfirm | null }>(r)),
  join: (store: string, dealId: string, of: HistoryOf) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/join`, post({ store, ...of })).then((r) => json<Joined>(r)),
  notSame: (store: string, dealId: string, of: HistoryOf) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/not-same`, post({ store, ...of })).then((r) => json<{ refused: true }>(r)),
  addDomain: (store: string, dealId: string, domain: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/domain`, post({ store, domain })).then((r) => json<DomainAdded>(r)),
  notDomain: (store: string, dealId: string, domain: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/domain/not`, post({ store, domain })).then((r) => json<{ refused: true }>(r)),
  undoDomain: (store: string, dealId: string, actionId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/domain/undo`, post({ store, actionId })).then((r) => json<{ undone: true }>(r)),
  undoJoin: (store: string, dealId: string, actionId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/join/undo`, post({ store, actionId })).then((r) => json<{ undone: true }>(r)),
  origins: (store: string, dealId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/origins?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' }).then((r) => json<{ origins: Origins | null }>(r)),
  steps: (store: string, dealId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/steps?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' }).then((r) => json<{ steps: DealSteps | null }>(r)),
  pinDraft: (store: string, dealId: string, draftId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/draft-pin`, post({ store, draftId })).then((r) => json<{ pinned: true }>(r)),
};
